import { AlertTriangle, CheckCircle2, LoaderCircle } from "lucide-react";
import { ComponentType, ReactNode } from "react";

import { errorMessage } from "../api";

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action && <div className="page-action">{action}</div>}
      <span className="page-header-signal" aria-hidden="true"><i /><i /><i /></span>
    </header>
  );
}

export function Section({
  title,
  description,
  children,
  action,
  danger = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  action?: ReactNode;
  danger?: boolean;
}) {
  return (
    <section className={`content-section${danger ? " danger-section" : ""}`}>
      <header className="section-heading">
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  detail,
}: {
  icon: ComponentType<{ "aria-hidden"?: boolean }>;
  title: string;
  detail: string;
}) {
  return (
    <div className="empty-state">
      <Icon aria-hidden />
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

export function LoadingState({ label = "正在载入" }: { label?: string }) {
  return (
    <div className="loading-state" role="status">
      <LoaderCircle className="spin" aria-hidden="true" />
      {label}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div>
        <strong>暂时无法完成请求</strong>
        <span>{errorMessage(error)}</span>
      </div>
      {retry && <button className="secondary-button" type="button" onClick={retry}>重试</button>}
    </div>
  );
}

export function MutationMessage({
  error,
  success,
}: {
  error?: unknown;
  success?: string | false;
}) {
  if (error) return <div className="inline-error" role="alert">{errorMessage(error)}</div>;
  if (!success) return null;
  return <div className="inline-success" role="status"><CheckCircle2 aria-hidden="true" />{success}</div>;
}

export function StatusBadge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "good" | "warning" | "danger" }) {
  return <span className={`status-badge ${tone}`}>{children}</span>;
}

export function shortId(value: string) {
  return `${value.slice(0, 8)}...${value.slice(-4)}`;
}
